import { CommandHelper } from '@app/shared/quick-commands/command-helper';
import { CommandCallback, CommandResultList, CommandResults } from '@app/shared/quick-commands/types';
import { Observable } from 'rxjs';

/**
 * Helper class for command results.
 */
export class CommandOptionFactory {
  constructor(private prefix: string) {}

  /**
   * Create a command action that prompts the user for all the passed options
   * and calls the specified callback function with all the selected results.
   * @param options Options to ask for.
   * @param cb Function that is called after all the options have been selected.
   */
  createAction<T extends any[]>(
    options: T,
    cb: (values: UnwrapOptionResults<T>) => CommandResults<any>
  ): CommandCallback<any> {
    return (query, results) => {
      if (results.length === options.length) {
        return cb(results.map((result) => result.value) as any);
      }

      const nextOption = options[results.length];
      return nextOption(query);
    };
  }

  /**
   * Create an option that will prompt the user to enter a value.
   * @param name Name of the option.
   * @param options Additional options, e.g. empty message, info message, etc.
   */
  input(name: string, options: Options<string> = {}): CommandOption<OptionResult> {
    return (query: string): CommandResultList<OptionResult> => {
      if (query.length === 0) {
        return CommandHelper.list<OptionResult>({
          emptyMessage: `quickCommands.commands.${this.prefix}.option.${name}.empty`,
          placeholder: `quickCommands.commands.${this.prefix}.option.${name}.placeholder`,
          ...options,
          values: [],
        });
      } else {
        return CommandHelper.list<OptionResult>({
          ...options,
          values: [
            {
              label: `quickCommands.commands.${this.prefix}.option.${name}`,
              labelParams: {
                value: query,
              },
              value: {
                name,
                value: query.trim(),
              },
            },
          ],
        });
      }
    };
  }

  /**
   * Create an option that prompts the user to select a predefined
   * value.
   * @param name Name of the option.
   * @param values Possible values to choose from.
   * @param options Additional options.
   */
  list<T extends string>(name: string, values: T[], options?: Options<T>): CommandOption<OptionResult<T>> {
    const { active: getActive, ...otherOptions } = options ?? {};

    return (_: string): CommandResultList<OptionResult<T>> => {
      const activeValue = getActive != null ? getActive() : null;

      return {
        filterAutomatically: true,
        placeholder: `quickCommands.commands.${this.prefix}.option.${name}.placeholder`,
        ...otherOptions,
        type: 'list',
        values: values.map((value) => {
          return {
            active: activeValue != null && activeValue === value,
            label: `quickCommands.commands.${this.prefix}.option.${name}.${value}`,
            value: {
              name,
              value,
            },
          };
        }),
      };
    };
  }

  /**
   * Create an option prompts the user to select a predefined
   * value. The predefined values are provided asynchronously.
   * @param name Name of the option.
   * @param valueProvider Function that asynchronously provides possible values.
   */
  list$(
    name: string,
    valueProvider: (query: string) => Observable<string[]>
  ): AsyncCommandOption<OptionResult> {
    return (query: string): Observable<CommandResultList<OptionResult>> => {
      return CommandHelper.list$(valueProvider(query), (values) => {
        return {
          placeholder: `quickCommands.commands.${this.prefix}.option.${name}.placeholder`,
          type: 'list',
          values: values.map((value) => {
            return {
              label: value,
              value: {
                name,
                value,
              },
            };
          }),
        };
      });
    };
  }
}

export interface CommandOption<T> {
  (query: string): CommandResultList<T>;
}

export interface AsyncCommandOption<T> {
  (query: string): Observable<CommandResultList<T>>;
}

export interface OptionResult<T extends string = string> {
  name: string;
  value: T;
}

type Options<T> = Omit<CommandResultList<T>, 'type' | 'values'> & {
  /**
   * Function that returns the currently active value (which is then marked as active).
   */
  active?: () => T;
};

/**
 * Get the generic type of the given array (UnwrapArray<T[]> => T)
 */
type UnwrapArray<T> = T extends Array<infer U> ? U : never;

/**
 * Get the generic type of the given ListOption (ListOption<T> => T).
 */
type UnwrapListOption<T> = T extends CommandOption<infer V> ? V : never;

/**
 * Get the generic type of the given OptionResult (OptionResult<T> => T).
 */
type UnwrapOptionResult<T> = T extends OptionResult<infer U> ? U : never;

/**
 * Get the generic type of the given array of ListOption
 * ([ListOption<string>, ListOption<number>] => [string, number]).
 */
type UnwrapOptions<T> = UnwrapListOption<UnwrapArray<T>>;

/**
 * Get the generic type of the given array of ListOption<OptionResult<T>>
 * ([ListOption<OptionResult<string>>, ListOption<OptionResult<number>>] => [string, number]).
 */
type UnwrapOptionResults<T> = UnwrapOptionResult<UnwrapOptions<T>>;
